import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Separator } from "../components/ui/separator";
import { Plus, PencilSimple, Trash, SquaresFour, UsersThree, Table } from "@phosphor-icons/react";
import { useRestaurant } from "../contexts/RestaurantContext";

const SECTION_COLORS = [
  "#81B29A", "#E07A5F", "#F4A261", "#3D405B", "#E9C46A", "#264653",
  "#6A5ACD", "#20B2AA", "#CD5C5C", "#DAA520", "#4682B4", "#9370DB",
  "#3CB371", "#FF6B6B", "#48BFE3", "#7B2D8E", "#F08080", "#2E8B57",
  "#DB7093", "#708090", "#B8860B", "#6B8E23",
];

function SectionForm({ onSubmit, buttonLabel, showFloor = true, form, setForm, floors, servers }) {
  return (
    <div className="space-y-4 mt-2">
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Section Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Patio, Bar Area, VIP" className="mt-1.5" data-testid="section-name-input" />
      </div>
      {showFloor && (
        <div>
          <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Floor</Label>
          <Select value={form.floor_id || "none"} onValueChange={(v) => setForm({ ...form, floor_id: v === "none" ? "" : v })}>
            <SelectTrigger className="mt-1.5" data-testid="section-floor-select"><SelectValue placeholder="Select floor" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none" disabled>Select a floor</SelectItem>
              {floors.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Color</Label>
        <div className="flex gap-2 flex-wrap">
          {SECTION_COLORS.map((c) => (
            <button key={c} className={`w-7 h-7 rounded-full border-2 transition-all ${form.color === c ? "ring-2 ring-offset-2" : ""}`} style={{ backgroundColor: c, borderColor: c, ringColor: c }} onClick={() => setForm({ ...form, color: c })} />
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Assigned Server</Label>
        <Select value={form.server_id || "none"} onValueChange={(v) => setForm({ ...form, server_id: v === "none" ? "" : v })}>
          <SelectTrigger className="mt-1.5" data-testid="section-server-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Unassigned</SelectItem>
            {servers.map((s) => (
              <SelectItem key={s.id} value={s.id}>
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
                  {s.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button onClick={onSubmit} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-section-btn">{buttonLabel}</Button>
    </div>
  );
}

export default function SectionsPage() {
  const { restId } = useRestaurant();
  const [sections, setSections] = useState([]);
  const [floors, setFloors] = useState([]);
  const [servers, setServers] = useState([]);
  const [tables, setTables] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showManageTables, setShowManageTables] = useState(false);
  const [managingSection, setManagingSection] = useState(null);
  const [form, setForm] = useState({ name: "", floor_id: "", color: "#81B29A", server_id: "" });

  const fetchSections = useCallback(async () => {
    try {
      const { data } = await api.get("/sections");
      setSections(data);
    } catch { toast.error("Failed to load sections"); }
  }, []);

  const fetchFloors = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/floors${params}`);
      setFloors(data);
    } catch { /* silent */ }
  }, [restId]);

  const fetchServers = useCallback(async () => {
    try {
      const { data } = await api.get("/servers");
      setServers(data);
    } catch { /* silent */ }
  }, []);

  const fetchAllTables = useCallback(async () => {
    try {
      const params = restId ? `?restaurant_id=${restId}` : "";
      const { data } = await api.get(`/tables/all${params}`);
      setTables(data);
    } catch { /* silent */ }
  }, [restId]);

  useEffect(() => { fetchSections(); fetchFloors(); fetchServers(); fetchAllTables(); }, [fetchSections, fetchFloors, fetchServers, fetchAllTables]);

  const createSection = async () => {
    if (!form.name.trim() || !form.floor_id) { toast.error("Name and floor are required"); return; }
    try {
      await api.post("/sections", {
        name: form.name, floor_id: form.floor_id, color: form.color,
        server_id: form.server_id || null,
      });
      setShowAdd(false);
      resetForm();
      fetchSections();
      toast.success("Section created");
    } catch { toast.error("Failed"); }
  };

  const updateSection = async () => {
    if (!editingSection) return;
    try {
      await api.put(`/sections/${editingSection.id}`, {
        name: form.name, color: form.color,
        server_id: form.server_id || null,
      });
      setShowEdit(false);
      setEditingSection(null);
      fetchSections();
      toast.success("Section updated");
    } catch { toast.error("Failed"); }
  };

  const deleteSection = async (id) => {
    try {
      await api.delete(`/sections/${id}`);
      fetchSections();
      fetchAllTables();
      toast.success("Section deleted — tables unassigned");
    } catch { toast.error("Failed"); }
  };

  const openEdit = (s) => {
    setEditingSection(s);
    setForm({ name: s.name, floor_id: s.floor_id, color: s.color, server_id: s.server_id || "" });
    setShowEdit(true);
  };

  const openManageTables = (s) => {
    setManagingSection(s);
    setShowManageTables(true);
  };

  const toggleTableInSection = async (table) => {
    if (!managingSection) return;
    const newSectionId = table.section_id === managingSection.id ? null : managingSection.id;
    try {
      await api.put(`/tables/${table.id}`, { section_id: newSectionId });
      fetchAllTables();
      toast.success(newSectionId ? `${table.name} added` : `${table.name} removed`);
    } catch { toast.error("Failed to update table"); }
  };

  const resetForm = () => setForm({ name: "", floor_id: "", color: "#81B29A", server_id: "" });

  const getFloorName = (floorId) => floors.find((f) => f.id === floorId)?.name || "Unknown";
  const getServerName = (serverId) => servers.find((s) => s.id === serverId)?.name || null;
  const getSectionTables = (sectionId) => tables.filter((t) => t.section_id === sectionId);

  // Group sections by floor
  const sectionsByFloor = {};
  sections.forEach((s) => {
    if (!sectionsByFloor[s.floor_id]) sectionsByFloor[s.floor_id] = [];
    sectionsByFloor[s.floor_id].push(s);
  });

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto" data-testid="sections-page">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Sections</h2>
          <p className="text-xs sm:text-sm text-stone-500 mt-1">{sections.length} section{sections.length !== 1 ? "s" : ""} across {Object.keys(sectionsByFloor).length} floor{Object.keys(sectionsByFloor).length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={() => { resetForm(); setShowAdd(true); }} style={{ backgroundColor: "#2F4F4F" }} size="sm" data-testid="add-section-page-btn" className="text-xs sm:text-sm">
          <Plus size={16} className="mr-1" /> New Section
        </Button>
      </div>

      {sections.length === 0 ? (
        <div className="text-center py-16 animate-fade-in">
          <SquaresFour size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <p className="text-stone-500 text-sm">No sections yet</p>
          <p className="text-xs text-stone-500 mt-1">Create sections to organize tables and assign servers</p>
        </div>
      ) : (
        <div className="space-y-6">
          {floors.map((floor) => {
            const floorSections = sectionsByFloor[floor.id] || [];
            if (floorSections.length === 0) return null;
            return (
              <div key={floor.id}>
                <h3 className="text-xs font-semibold uppercase tracking-widest text-stone-500 mb-3">{floor.name}</h3>
                <div className="space-y-3">
                  {floorSections.map((s, idx) => {
                    const sectionTables = getSectionTables(s.id);
                    const serverName = getServerName(s.server_id);
                    const totalSeats = sectionTables.reduce((a, t) => a + t.seats, 0);
                    const occupiedCount = sectionTables.filter((t) => t.status === "occupied").length;

                    return (
                      <div key={s.id} className="bg-white rounded-lg border border-stone-200 overflow-hidden animate-fade-in" style={{ animationDelay: `${idx * 50}ms`, borderLeftWidth: "4px", borderLeftColor: s.color }} data-testid={`section-card-${s.id}`}>
                        <div className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: s.color + "20" }}>
                                <SquaresFour size={20} weight="duotone" style={{ color: s.color }} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="font-semibold text-stone-800" style={{ fontFamily: "Work Sans" }}>{s.name}</h4>
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                                </div>
                                <div className="flex items-center gap-3 mt-0.5 text-xs text-stone-500">
                                  {serverName ? (
                                    <span className="flex items-center gap-1">
                                      <UsersThree size={12} />
                                      <span className="font-medium text-stone-700">{serverName}</span>
                                    </span>
                                  ) : (
                                    <span className="text-stone-500 italic">No server assigned</span>
                                  )}
                                  <span className="flex items-center gap-1"><Table size={12} /> {sectionTables.length} table{sectionTables.length !== 1 ? "s" : ""}</span>
                                  <span>{totalSeats} seats</span>
                                  {occupiedCount > 0 && <Badge className="bg-red-100 text-red-600 text-[9px]">{occupiedCount} occupied</Badge>}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="outline" size="sm" onClick={() => openManageTables(s)} className="text-xs h-8" data-testid={`manage-tables-${s.id}`}>
                                <Table size={14} className="mr-1" /> Tables
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="h-8 w-8 p-0" data-testid={`edit-section-${s.id}`}>
                                <PencilSimple size={14} />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600" onClick={() => deleteSection(s.id)} data-testid={`delete-section-${s.id}`}>
                                <Trash size={14} />
                              </Button>
                            </div>
                          </div>

                          {/* Table chips */}
                          {sectionTables.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-stone-100">
                              {sectionTables.map((t) => {
                                const statusColor = t.status === "available" ? "#81B29A" : t.status === "occupied" ? "#E07A5F" : t.status === "reserved" ? "#F4A261" : "#9CA3AF";
                                return (
                                  <div key={t.id} className="flex items-center gap-1 px-2 py-1 rounded-md border border-stone-100 text-xs bg-stone-50" data-testid={`section-table-chip-${t.name}`}>
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }} />
                                    <span className="font-medium text-stone-700">{t.name}</span>
                                    <span className="text-stone-500">{t.seats}s</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Section */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-section-dialog">
          <DialogHeader><DialogTitle>New Section</DialogTitle></DialogHeader>
          <SectionForm onSubmit={createSection} buttonLabel="Create Section" form={form} setForm={setForm} floors={floors} servers={servers} />
        </DialogContent>
      </Dialog>

      {/* Edit Section */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent data-testid="edit-section-dialog">
          <DialogHeader><DialogTitle>Edit Section</DialogTitle></DialogHeader>
          <SectionForm onSubmit={updateSection} buttonLabel="Save Changes" showFloor={false} form={form} setForm={setForm} floors={floors} servers={servers} />
        </DialogContent>
      </Dialog>

      {/* Manage Tables in Section */}
      <Dialog open={showManageTables} onOpenChange={setShowManageTables}>
        <DialogContent data-testid="manage-tables-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {managingSection && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: managingSection.color }} />}
              {managingSection?.name} — Tables
            </DialogTitle>
          </DialogHeader>
          {managingSection && (
            <div className="mt-2">
              <p className="text-xs text-stone-500 mb-3">Click tables to add or remove them from this section. Tables in this section will glow with <span className="font-semibold" style={{ color: managingSection.color }}>this color</span> on the floor plan.</p>
              {(() => {
                const floorTables = tables.filter((t) => t.floor_id === managingSection.floor_id);
                const inSection = floorTables.filter((t) => t.section_id === managingSection.id);
                const notInSection = floorTables.filter((t) => t.section_id !== managingSection.id);
                return (
                  <div className="space-y-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">
                        In Section ({inSection.length})
                      </p>
                      {inSection.length === 0 ? (
                        <p className="text-xs text-stone-500 py-2">No tables assigned yet</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {inSection.map((t) => (
                            <button
                              key={t.id}
                              onClick={() => toggleTableInSection(t)}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all text-sm font-medium"
                              style={{ borderColor: managingSection.color, backgroundColor: managingSection.color + "15", color: managingSection.color }}
                              data-testid={`section-toggle-${t.name}`}
                            >
                              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: managingSection.color }} />
                              {t.name}
                              <span className="text-xs opacity-60">{t.seats}s</span>
                              <Trash size={12} className="ml-1 opacity-40" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Separator />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold mb-2">
                        Available to Add ({notInSection.length})
                      </p>
                      {notInSection.length === 0 ? (
                        <p className="text-xs text-stone-500 py-2">All tables on this floor are assigned</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {notInSection.map((t) => {
                            const otherSection = sections.find((sec) => sec.id === t.section_id);
                            return (
                              <button
                                key={t.id}
                                onClick={() => toggleTableInSection(t)}
                                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 hover:border-stone-400 transition-all text-sm font-medium text-stone-700 bg-white"
                                data-testid={`section-add-${t.name}`}
                              >
                                <Plus size={12} />
                                {t.name}
                                <span className="text-xs text-stone-500">{t.seats}s</span>
                                {otherSection && (
                                  <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ backgroundColor: otherSection.color + "20", color: otherSection.color }}>
                                    {otherSection.name}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
